import { useEffect, useRef, useMemo } from 'react';
import useAsyncState from 'hooks/useAsyncState.hook';
import useComponent from 'hooks/useComponent.hook';
import ConversationsHeader from 'views/chat/components/headers/ConversationsHeader.component';
import ContactConversationsList from 'views/chat/components/lists/ContactConversationsList.component.js';
import NeoSlideMenu from 'design/design_components/neo/menu/NeoSlideMenu.base';
import NeoButtonMain from 'design/design_components/neo/button/NeoButtonMain.base';
import TOAST from 'consts/toast.consts';
import useChat from 'hooks/chat/useChat.hook';
import { useChatConversationsExpired } from 'hooks/chat/useChatConversationsExpired.hook';
import ChatService from 'services/chat.service';
import ReopenAssignConversationDialog from '../dialogs/ReopenAssignConversationDialog.component';
import useMessages from 'hooks/useMessages.hook';

const FILTER_STATUS = {
  ALL: 1,
  TIME_ELAPSED: 2,
};

const FILTER_ORDER = {
  LONGER_WAITING_TIME: 1,
  MORE_RECENT: 2
};

export default function ConversationsExpiredSection() {
  const chat = useChat();
  const messages = useMessages();
  const { data: conversationsExpired, loading } = useChatConversationsExpired({ lineId: chat.selected.line.id });

  const asyncState = useAsyncState();
  const component = useComponent({
    name: 'ConversationsExpiredSection',
    state: {
      transition: '',
      filterStatusActive: Object.values(FILTER_STATUS),
      filterOrderActive: FILTER_ORDER.LONGER_WAITING_TIME,
      showAssignConversationDialog: false,
      selecteds: [],
    }
  });

  useEffect(() => {
    setTimeout(() => {
      component.setState((state) => {
        state.transition = "transition";
      })
    }, 1)
  }, []);

  const assignConversationsExpired = async (agentId, conversations = []) => {
    const response = await ChatService.assignConversationsExpired(chat.selected.line.id, agentId, conversations);
    if(response.success) {
      return {success: true};
    }
    return {success: false};
  }
  
  const orderConversationsByUserWaiting = (conversations) => {
    const newArrayOfConversations = [];

    const conversationsWithMessageUser  = orderConversationsByRecentAsc(conversations).reverse();

    newArrayOfConversations.unshift(...conversationsWithMessageUser);
    return newArrayOfConversations;
  }

  const orderConversationsByRecentAsc = (conversations) => {
    return [...conversations].sort((conversationA, conversationB) => {
      return new Date(conversationB.lastMessageSentAt) - new Date(conversationA.lastMessageSentAt);
    });
  }

  const applyFilters = (conversationsExpired = [], filters = [], order = FILTER_ORDER.LONGER_WAITING_TIME) => {
    conversationsExpired = conversationsExpired.filter((conversationExpired) => {
      let add = filters.includes(FILTER_STATUS.ALL);

      if(!add && filters.includes(FILTER_STATUS.TIME_ELAPSED)) {
        add = Boolean(conversationExpired?.timeElapsedLastMessage);
      }

      return add;
    });

    switch (order) {
      case FILTER_ORDER.LONGER_WAITING_TIME:
        conversationsExpired = orderConversationsByUserWaiting(conversationsExpired);
        break;
      case FILTER_ORDER.MORE_RECENT:
        conversationsExpired = orderConversationsByRecentAsc(conversationsExpired);
        break;
      default:
        break;
    }

    return conversationsExpired;
  }

  const filteredConversationsExpired = useMemo(() => 
    applyFilters(conversationsExpired, component.state.filterStatusActive, component.state.filterOrderActive)
  , [conversationsExpired, component.state.filterStatusActive, component.state.filterOrderActive]);
  
  const ButtonAssign = () => {
    return <>
      <NeoButtonMain type="button" icon="pi pi-user-plus" onClick={(event) => {
        if (component.state.selecteds.length === 0) {
          messages.showToast(TOAST.SEVERITY.WARNING, 'Sin conversaciones', 'Selecciona al menos 1 conversación para asignar.');
        } else {
          component.setState((state) => {
            state.showAssignConversationDialog = true;
          })
        }
      }} />
    </>
  }

  const FilterOrder = () => {
    const menu = useRef(null);
    const items = [
      {
        label: 'Cliente con más tiempo de abandono',
        icon: component.state.filterOrderActive === FILTER_ORDER.LONGER_WAITING_TIME ? 'pi pi-circle-on' : 'pi pi-circle-off',
        command: () => {
          component.setState((state) => {
            state.filterOrderActive = FILTER_ORDER.LONGER_WAITING_TIME;
          })
        }
      },
      {
        label: 'Mensaje más reciente',
        icon: component.state.filterOrderActive === FILTER_ORDER.MORE_RECENT ? 'pi pi-circle-on' : 'pi pi-circle-off',
        command: () => {
          component.setState((state) => {
            state.filterOrderActive = FILTER_ORDER.MORE_RECENT;
          })
        }
      }
    ];

    return <>
      <NeoSlideMenu ref={menu} model={items} popup className='FilterOrder'/>
      <NeoButtonMain type="button" icon="pi pi-sort-amount-down" onClick={(event) => menu.current.toggle(event)} />
    </>
  }

  const totalConversationsExpired = conversationsExpired.length;

  return (
    <div className={component.classNames(component.name, component.state.transition)}>
      <ConversationsHeader
        isLoading={asyncState.isLoading}
        line={chat.selected.line}
        agent={{
          name: "Conversaciones expiradas",
          area: `Total: ${totalConversationsExpired}`,
        }}
        filters={[FilterOrder, ButtonAssign]}
      />

      <ContactConversationsList
        isLoading={asyncState.isLoading}
        conversations={filteredConversationsExpired}
        conversationSelected={chat.selected.conversation}
        selecteds={component.state.selecteds}
        enableSelect={true}
        onSelect={(conversation, event) => {
          const { chatId } = conversation;
          const { checked } = event;
          component.setState((state) => {
            if (checked) {
              state.selecteds.push(chatId);
            } else {
              state.selecteds = state.selecteds.filter((item) => {
                return item !== chatId;
              })
            }
          })
        }}
        onConversationSelect={(conversation) => {
          chat.dispatch((TYPE) => {
            return {
              type: TYPE.CONVERSATION.SELECTED.TYPE,
              payload: conversation.id
            }
          })
        }}
      />

      <ReopenAssignConversationDialog
        title="Asignar"
        line={chat.selected.line}
        visible={component.state.showAssignConversationDialog}
        visibleSetter={(visible) => {
          component.setState((state) => {
            state.showAssignConversationDialog = visible;
          })
        }}
        onSubmit={(agentId) => {
          assignConversationsExpired(agentId, component.state.selecteds);
        }}
      />
    </div>
  );
}